import { createContext, useContext, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle2 } from "lucide-react";
import { LOGO_HORIZONTAL } from "@/data/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AppointmentContext = createContext({ open: () => {}, close: () => {} });

export const useAppointment = () => useContext(AppointmentContext);

// ─── Modal ───
const Modal = ({ isOpen, onClose }) => {
  const [phase, setPhase] = useState("form"); // form | success
  const [name, setName] = useState("");
  // phone stores ONLY the 10 digits after +38 (e.g. "0501234567")
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setPhase("form");
    setName("");
    setPhone("");
    setErrors({});
    setSubmitting(false);
  };

  const handleClose = () => {
    onClose();
    // Reset after exit animation
    setTimeout(reset, 300);
  };

  // Format raw digits into "(0XX) XXX XX XX"
  const formatPhone = (digits) => {
    const d = digits.slice(0, 10);
    if (!d) return "";
    if (d.length <= 3) return `(${d}`;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    if (d.length <= 8) return `(${d.slice(0, 3)}) ${d.slice(3, 6)} ${d.slice(6)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)} ${d.slice(6, 8)} ${d.slice(8, 10)}`;
  };

  const onPhoneChange = (e) => {
    let digits = e.target.value.replace(/\D/g, "");
    // Strip leading 38 if user accidentally typed it
    if (digits.startsWith("38")) digits = digits.slice(2);
    setPhone(digits.slice(0, 10));
    if (errors.phone) setErrors((p) => ({ ...p, phone: null }));
  };

  const validate = () => {
    const e = {};
    if (!name.trim() || name.trim().length < 2) e.name = "Вкажіть ім'я";
    if (phone.length !== 10 || phone[0] !== "0") e.phone = "Введіть 10 цифр номера, що починається з 0";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/appointments`, {
        name: name.trim(),
        phone: `+38${phone}`,
        service: "",
        doctor: "",
        message: "",
      });
      setSubmitting(false);
      setPhase("success");
    } catch (err) {
      setSubmitting(false);
      setErrors({ submit: "Не вдалося надіслати. Спробуйте ще раз або зателефонуйте нам." });
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={handleClose}
          data-testid="appointment-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 20 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-[460px] bg-white rounded-3xl p-8 sm:p-10 shadow-[0_24px_80px_-20px_rgba(0,0,0,0.4)]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#F5F5F5] hover:bg-[#E5E5E5] flex items-center justify-center text-[#666] transition-colors"
              aria-label="Close"
              data-testid="appointment-modal-close"
            >
              <X className="w-5 h-5" />
            </button>

            {phase === "form" && (
              <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.08 }}
              >
                {/* Logo */}
                <div className="flex justify-center mb-6">
                  <img src={LOGO_HORIZONTAL} alt="Центр мікрохірургії ока «Ваш зір»" className="h-16" />
                </div>

                <h3 className="text-center text-lg sm:text-xl font-semibold text-[#333] leading-snug mb-7">
                  Заповніть, будь ласка, форму,<br />щоб ми могли зв'язатися з Вами
                </h3>

                <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                  <div>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => { setName(e.target.value); if (errors.name) setErrors((p) => ({ ...p, name: null })); }}
                      placeholder="Ім'я"
                      className={`w-full h-14 px-5 rounded-2xl bg-[#F5F5F5] border-2 text-base text-[#333] placeholder:text-[#999] outline-none transition-colors ${errors.name ? "border-[#DC2626]" : "border-transparent focus:border-[#E8590A]"}`}
                      data-testid="appointment-name-input"
                      autoComplete="name"
                    />
                    {errors.name && <p className="text-xs text-[#DC2626] mt-1.5 px-2">{errors.name}</p>}
                  </div>

                  <div>
                    <div className={`flex items-center w-full h-14 rounded-2xl bg-[#F5F5F5] border-2 overflow-hidden transition-colors ${errors.phone ? "border-[#DC2626]" : "border-transparent focus-within:border-[#E8590A]"}`}>
                      <span className="pl-5 pr-3 text-base text-[#333] font-medium select-none border-r border-[#E0E0E0] mr-3 h-full flex items-center">
                        +38
                      </span>
                      <input
                        type="tel"
                        inputMode="numeric"
                        value={formatPhone(phone)}
                        onChange={onPhoneChange}
                        placeholder="(0XX) XXX XX XX"
                        className="flex-1 h-full bg-transparent text-base text-[#333] placeholder:text-[#999] outline-none pr-5"
                        data-testid="appointment-phone-input"
                        autoComplete="tel-national"
                        maxLength={16}
                      />
                    </div>
                    {errors.phone && <p className="text-xs text-[#DC2626] mt-1.5 px-2">{errors.phone}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-14 rounded-2xl bg-[#E8590A] text-white font-semibold text-base hover:bg-[#D14D08] active:scale-[0.99] disabled:opacity-70 transition-all"
                    data-testid="appointment-submit-btn"
                  >
                    {submitting ? "Відправляємо..." : "Записатись"}
                  </button>

                  {errors.submit && (
                    <p className="text-sm text-[#DC2626] text-center mt-1">{errors.submit}</p>
                  )}
                </form>
              </motion.div>
            )}

            {phase === "success" && (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
                className="text-center py-4"
                data-testid="appointment-success"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, damping: 16, delay: 0.1 }}
                  className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#FFF3ED] mb-6"
                >
                  <CheckCircle2 className="w-12 h-12 text-[#E8590A]" strokeWidth={1.75} />
                </motion.div>

                <h3 className="text-2xl font-semibold text-[#333] mb-3">Дякуємо!</h3>
                <p className="text-base text-[#666] leading-relaxed mb-8 max-w-sm mx-auto">
                  Ми зв'яжемося з Вами якнайшвидше
                </p>

                <button
                  onClick={handleClose}
                  className="w-full h-14 rounded-2xl bg-[#E8590A] text-white font-semibold text-base hover:bg-[#D14D08] active:scale-[0.99] transition-all"
                  data-testid="appointment-success-close"
                >
                  Очікую!
                </button>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ─── Provider ───
export const AppointmentProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  return (
    <AppointmentContext.Provider value={{ open, close }}>
      {children}
      <Modal isOpen={isOpen} onClose={close} />
    </AppointmentContext.Provider>
  );
};
